// External

import React, { useEffect, useState } from 'react';
import { imageLoadPoolManager, cache as csCache, Enums as csEnums } from '@cornerstonejs/core';
import PropTypes from 'prop-types';
import i18n from '@ohif/i18n';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter, type BrowserRouterProps } from 'react-router-dom';

import Compose from './routes/Mode/Compose';
import {
  ExtensionManager,
  CommandsManager,
  HotkeysManager,
  ServiceProvidersManager,
  SystemContextProvider,
  ViewportRefsProvider,
} from '@ohif/core';
import {
  ThemeWrapper as ThemeWrapperNext,
  NotificationProvider,
  ViewportGridProvider,
  DialogProvider,
  CineProvider,
  TooltipProvider,
  Modal as ModalNext,
  ManagedDialog,
  ModalProvider,
  ViewportDialogProvider,
  UserAuthenticationProvider,
} from '@ohif/ui-next';
// Viewer Project
// TODO: Should this influence study list?
import { AppConfigProvider } from '@state';
import createRoutes from './routes';
import appInit from './appInit.js';
import OpenIdConnectRoutes from './utils/OpenIdConnectRoutes';
import { ShepherdJourneyProvider } from 'react-shepherd';
import './App.css';
import { MyProvider } from './contexts/context';
import { Provider } from 'react-redux';
import store from './redux/stores/store';

let commandsManager: CommandsManager,
  extensionManager: ExtensionManager,
  servicesManager: AppTypes.ServicesManager,
  serviceProvidersManager: ServiceProvidersManager,
  hotkeysManager: HotkeysManager;

const routerFutureFlags: BrowserRouterProps['future'] = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

const RUNTIME_ERROR_OVERLAY_IDENTIFIERS = [
  'webpack-dev-server-client-overlay',
  'react-refresh-overlay',
  'react-error-overlay',
];

function hideRuntimeErrorOverlayElement(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  element.style.setProperty('display', 'none', 'important');
  element.style.setProperty('visibility', 'hidden', 'important');
  element.setAttribute('aria-hidden', 'true');
}

function isRuntimeErrorOverlayElement(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const id = (element.id || '').toLowerCase();
  const className =
    typeof element.className === 'string' ? element.className.toLowerCase() : '';
  const title = (element.getAttribute('title') || '').toLowerCase();
  const src = (element.getAttribute('src') || '').toLowerCase();
  const text = (element.textContent || '').slice(0, 200).toLowerCase();

  const hasKnownIdentifier = RUNTIME_ERROR_OVERLAY_IDENTIFIERS.some(identifier => {
    return (
      id.includes(identifier) ||
      className.includes(identifier) ||
      title.includes(identifier) ||
      src.includes(identifier)
    );
  });

  if (hasKnownIdentifier) {
    return true;
  }

  return (
    (element.tagName === 'DIV' || element.tagName === 'IFRAME') &&
    text.includes('uncaught runtime errors')
  );
}

function App({
  config = {
    /**
     * Relative route from domain root that OHIF instance is installed at.
     * For example:
     *
     * Hosted at: https://ohif.org/where-i-host-the/viewer/
     * Value: `/where-i-host-the/viewer/`
     * */
    routerBasename: '/',
    /**
     *
     */
    showLoadingIndicator: true,
    showStudyList: true,
    oidc: [],
    extensions: [],
  },
  defaultExtensions = [],
  defaultModes = [],
}) {
  const [init, setInit] = useState(null);
  const showRuntimeErrorOverlay = init?.appConfig?.showRuntimeErrorOverlay ?? true;

  useEffect(() => {
    const run = async () => {
      appInit(config, defaultExtensions, defaultModes).then(setInit).catch(console.error);
    };

    run();
  }, []);

  useEffect(() => {
    if (!init || showRuntimeErrorOverlay) {
      return;
    }

    const styleId = 'ohif-hide-runtime-error-overlays';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        #webpack-dev-server-client-overlay,
        iframe#webpack-dev-server-client-overlay,
        #react-refresh-overlay,
        iframe#react-refresh-overlay {
          display: none !important;
          visibility: hidden !important;
        }
      `;
      document.head.appendChild(style);
    }

    const hideExistingOverlays = () => {
      const candidates = document.querySelectorAll('iframe, div');
      candidates.forEach(element => {
        if (isRuntimeErrorOverlayElement(element)) {
          hideRuntimeErrorOverlayElement(element);
        }
      });
    };

    hideExistingOverlays();

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (!(node instanceof Element)) {
            return;
          }

          if (isRuntimeErrorOverlayElement(node)) {
            hideRuntimeErrorOverlayElement(node);
          }

          const nestedCandidates = node.querySelectorAll?.('iframe, div');
          nestedCandidates?.forEach(element => {
            if (isRuntimeErrorOverlayElement(element)) {
              hideRuntimeErrorOverlayElement(element);
            }
          });
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      const styleNode = document.getElementById(styleId);
      styleNode?.remove();
    };
  }, [init, showRuntimeErrorOverlay]);

  if (!init) {
    return null;
  }

  // Set above for named export
  commandsManager = init.commandsManager;
  extensionManager = init.extensionManager;
  servicesManager = init.servicesManager;
  serviceProvidersManager = init.serviceProvidersManager;
  hotkeysManager = init.hotkeysManager;

  // Set appConfig
  const appConfigState = init.appConfig;
  const { routerBasename, modes, dataSources, oidc, showStudyList } = appConfigState;

  // Probe WebGL2 capabilities — used both for max3DTextureSize and to decide
  // whether 16-bit normalized 3D textures (used by VTK volume rendering) can
  // actually be allocated on this device.
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2');

  let supportsNorm16 = false;
  if (gl) {
    const max3DTextureSize = gl.getParameter(gl.MAX_3D_TEXTURE_SIZE);
    appConfigState.max3DTextureSize = max3DTextureSize;

    // EXT_texture_norm16 is REQUIRED for useNorm16Texture to actually work.
    // iOS/iPadOS Safari does not implement this extension; some Android
    // browsers also lack it. Forcing useNorm16Texture: true on those
    // devices makes texImage3D silently reject the upload, leaving MPR/3D
    // viewports as black canvases (volume metadata loads, slices count,
    // W/L computes, but the GPU texture is empty).
    try {
      supportsNorm16 = !!gl.getExtension('EXT_texture_norm16');
    } catch (_e) {
      supportsNorm16 = false;
    }
  }

  // Detect mobile/iOS devices using userAgent first (more reliable than the
  // deprecated navigator.platform), then fall back to MacIntel + any touch
  // signal for iPadOS 13+ which masquerades as macOS — including Desktop-
  // mode iPads where maxTouchPoints can be 0 or 1 (the previous `> 1`
  // check missed those, leaving them with useNorm16Texture=true and the
  // full webWorker count, both of which break MPR/3D on iPad Safari).
  const __ua = (navigator && navigator.userAgent) || '';
  const __platform = navigator.platform || '';
  const __touchPoints = navigator.maxTouchPoints || 0;
  const __hasTouchEvents =
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || (navigator as any).msMaxTouchPoints > 0);
  const isIOS =
    /iPad|iPhone|iPod/i.test(__ua) ||
    /iPad|iPhone|iPod/.test(__platform) ||
    (__platform === 'MacIntel' && (__touchPoints > 0 || __hasTouchEvents));
  const isAndroid = /Android/i.test(__ua);
  const isMobileDevice = isIOS || isAndroid;

  if (isMobileDevice) {
    const deviceMemory =
      (navigator as unknown as { deviceMemory?: number }).deviceMemory || 4;

    appConfigState.isMobile = true;
    appConfigState.gpuTier = { tier: 1 };
    appConfigState.preferSizeOverAccuracy = true;
    // Was: appConfigState.useNorm16Texture = true;
    // iPad/iPhone Safari does not implement EXT_texture_norm16. Forcing
    // norm16 textures was causing all MPR/3D viewports to render black
    // even though the volume, W/L, slice count and overlays all loaded.
    // preferSizeOverAccuracy: true (above) routes VTK through 8-bit
    // textures which iOS Safari fully supports.
    appConfigState.useNorm16Texture = false;

    // Per-device tuning. Concurrent decode workers stay LOW (Safari is
    // unstable with > 2 workers), but concurrent FETCH requests stay
    // HIGH because metadata + bulkdata-URI resolution requires hundreds
    // of small round-trips per study and HTTP/2 multiplexes them well.
    // The earlier "prefetch: 3" was causing 32-second setVolumes() calls
    // on 700-slice studies (700 instances ÷ 3 in flight × ~100ms RTT).
    if (deviceMemory <= 4) {
      appConfigState.maxCacheSize = 256 * 1024 * 1024; // 256MB
      appConfigState.maxNumberOfWebWorkers = 1;
      appConfigState.maxNumRequests = {
        interaction: 30,
        thumbnail: 15,
        prefetch: 20,
      };
    } else if (deviceMemory <= 8) {
      appConfigState.maxCacheSize = 512 * 1024 * 1024; // 512MB
      appConfigState.maxNumberOfWebWorkers = 2;
      appConfigState.maxNumRequests = {
        interaction: 50,
        thumbnail: 25,
        prefetch: 30,
      };
    }

    // ── DCMCloud: sync cornerstone cache to the mobile cap ──────────────────
    // init.tsx called cache.setMaxCacheSize(appConfig.maxCacheSize) before this
    // component ran, using the desktop value from default.js (1 GB).  The mobile
    // overrides above must be pushed to the live cache explicitly, otherwise
    // Cornerstone continues to hold up to 1 GB of decoded volume data while
    // Safari is actively trying to reclaim memory from the tab — exactly the
    // condition that triggers iOS GPU context eviction on older iPads.
    if (appConfigState.maxCacheSize) {
      csCache.setMaxCacheSize(appConfigState.maxCacheSize);
      console.log('[DCMCloud] cornerstone cache capped for mobile:', appConfigState.maxCacheSize);
    }
    // ─────────────────────────────────────────────────────────────────────────

    if (isIOS && appConfigState.max3DTextureSize > 2048) {
      // Cap at 2048 explicitly — iPad's reported max3D varies between
      // models, and we don't want to allocate textures larger than the
      // smallest iPad we support. (Was 1024, raised to 2048 because
      // modern iPads handle this fine and 1024 was too restrictive.)
      appConfigState.max3DTextureSize = 2048;
    }

    // ── DCMCloud: sync imageLoadPoolManager with mobile concurrency caps ──────
    // init.tsx configures imageLoadPoolManager.maxNumRequests during appInit,
    // which runs BEFORE this component executes. At that point appConfig still
    // holds the desktop defaults from default.js (interaction:100, prefetch:50).
    // The mobile overrides set above (e.g. prefetch:20 for ≤4GB devices) must
    // be pushed to the pool manager explicitly, otherwise the iPad still uses
    // the desktop values and fires ~50 concurrent WADO-RS range requests, which
    // saturates Safari's HTTP/2 connection pool and stalls MPR / 3D loading.
    if (appConfigState.maxNumRequests) {
      const RT = csEnums.RequestType;
      imageLoadPoolManager.maxNumRequests = {
        [RT.Interaction]: appConfigState.maxNumRequests.interaction ?? 20,
        [RT.Thumbnail]:   appConfigState.maxNumRequests.thumbnail   ?? 10,
        [RT.Prefetch]:    appConfigState.maxNumRequests.prefetch    ?? 25,
        [RT.Compute]:     appConfigState.maxNumRequests.compute     ?? 10,
      };
      console.log('[DCMCloud] imageLoadPoolManager synced for mobile:', imageLoadPoolManager.maxNumRequests);
    }
    // ─────────────────────────────────────────────────────────────────────────
  }

  // Defensive backstop: regardless of platform-detection branch above, if
  // the user's GPU genuinely does not expose EXT_texture_norm16, never
  // ask VTK to use it. This protects desktop Safari, embedded WebViews,
  // headless browsers, and any iPad/Android the UA detection above might
  // misclassify (e.g. iPadOS Desktop-mode with low touch points).
  if (!supportsNorm16) {
    appConfigState.useNorm16Texture = false;
  }

  // eslint-disable-next-line no-console
  console.log('[DCMCloud] platform detect', {
    isIOS,
    isAndroid,
    isMobileDevice,
    supportsNorm16,
    useNorm16Texture: appConfigState.useNorm16Texture,
    useSharedArrayBuffer: appConfigState.useSharedArrayBuffer,
    crossOriginIsolated: !!(typeof window !== 'undefined' && window.crossOriginIsolated),
    max3DTextureSize: appConfigState.max3DTextureSize,
    ua: __ua,
  });

  const {
    uiDialogService,
    uiModalService,
    uiViewportDialogService,
    viewportGridService,
    cineService,
    userAuthenticationService,
    uiNotificationService,
    customizationService,
  } = servicesManager.services;

  const providers = [
    [AppConfigProvider, { value: appConfigState }],
    [UserAuthenticationProvider, { service: userAuthenticationService }],
    [I18nextProvider, { i18n }],
    [ThemeWrapperNext],
    [SystemContextProvider, { commandsManager, extensionManager, hotkeysManager, servicesManager }],
    [ViewportRefsProvider],
    [ViewportGridProvider, { service: viewportGridService }],
    [ViewportDialogProvider, { service: uiViewportDialogService }],
    [CineProvider, { service: cineService }],
    [NotificationProvider, { service: uiNotificationService }],
    [TooltipProvider],
    [DialogProvider, { service: uiDialogService, dialog: ManagedDialog }],
    [ModalProvider, { service: uiModalService, modal: ModalNext }],
    [ShepherdJourneyProvider],
    [Provider, { store: store }],
    [MyProvider],
  ];

  // Loop through and register each of the service providers registered with the ServiceProvidersManager.
  const providersFromManager = Object.entries(serviceProvidersManager.providers);
  if (providersFromManager.length > 0) {
    providersFromManager.forEach(([serviceName, provider]) => {
      providers.push([provider, { service: servicesManager.services[serviceName] }]);
    });
  }

  const CombinedProviders = ({ children }) => Compose({ components: providers, children });

  let authRoutes = null;

  // Should there be a generic call to init on the extension manager?
  customizationService.init(extensionManager);

  // Use config to create routes
  const appRoutes = createRoutes({
    modes,
    dataSources,
    extensionManager,
    servicesManager,
    commandsManager,
    hotkeysManager,
    routerBasename,
    showStudyList,
  });

  if (oidc) {
    authRoutes = (
      <OpenIdConnectRoutes
        oidc={oidc}
        routerBasename={routerBasename}
        userAuthenticationService={userAuthenticationService}
      />
    );
  }

  return (
    <CombinedProviders>
      <BrowserRouter
        basename={routerBasename}
        future={routerFutureFlags}
      >
        {authRoutes}
        {appRoutes}
      </BrowserRouter>
    </CombinedProviders>
  );
}

App.propTypes = {
  config: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      routerBasename: PropTypes.string.isRequired,
      oidc: PropTypes.array,
      whiteLabeling: PropTypes.object,
      extensions: PropTypes.array,
    }),
  ]).isRequired,
  /* Extensions that are "bundled" or "baked-in" to the application.
   * These would be provided at build time as part of they entry point. */
  defaultExtensions: PropTypes.array,
  /* Modes that are "bundled" or "baked-in" to the application.
   * These would be provided at build time as part of they entry point. */
  defaultModes: PropTypes.array,
};

export default App;

export { commandsManager, extensionManager, servicesManager };
